import { Ico_Filter_Component, Ico_SortAscending, Ico_SortDescending, Ico_Warning, PageTitle } from '@sagecentral/core-ui';
import { Box, Button, CircularProgress, Grid, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from "@mui/material";
import useWorkItemPossibleStatus from "hooks/work-items/useWorkItemPossibleStatus";
import { useEffect, useMemo, useState } from "react";
import { useWorkItems } from "hooks/work-items";
import { WorkItem, WorkItemSearchCriteria } from "hooks/work-items/useWorkItem";
import { GridBox, GridNoRowsOverlay, GridToolbar } from "components/shared/grids";
import { GetAutocompleteResult } from "hooks/work-items/useWorkItemAutocompleteResource";
import { DataGridPro, DataGridProProps, GridColDef, GridFooter, GridFooterContainer, GridRenderCellParams, GridRowModel, GridRowModelUpdate, GridRowProps, GridRowSelectionModel, GridTreeNodeWithRender, useGridApiRef } from "@mui/x-data-grid-pro";
import useSaveWorkItems from 'hooks/work-items/useSaveWorkItem';
import moment from "moment";
import WorkItemRow from './shared/WorkItemRow';
import BulkUpdateOpertionsStatus from './shared/BulkUpdateOperationsStatus';
import BulkUpdateOpertionsResponsibilities from './shared/BulkUpdateOperationsResponsibilities';
import useWorkItemsWithResources, { AssignedUser, ResponsibilityWithResources, WorkItemWithResources } from 'hooks/work-items/useWorkItemWithResources';
import { IChangedWorkItemResourceAssigne, ISaveWorkItemResourceAssigne } from 'models/work-items/IChangedWorkItemResourceAssignee';
import BulkUpdateSearch from './shared/BulkUpdateSearch';
import useBulkUpdateSaveWorkItemResources from 'hooks/work-items/useBulkUpdateSaveWorkItemResources';
import WorkItemResourceReviewChanges from './WorkItemResourceReviewChanges';
import { GroupByColumnRenderCell, useWorkItemColumns as useWorkItemStatusColumns, useWorkItemResourcesColumns } from './grid'
import { useBulkUpdateWorkItemPermissions } from "hooks/work-items";
import { Helmet } from 'react-helmet';

const NoRowsOverlay = (searched: boolean) => {
    return (
        <GridNoRowsOverlay text={searched ? 'No results found.' : ' '} />
    );
};

const CustomFooter = (total: number) => {

    return (
        <GridFooterContainer>
            <Box sx={{ pl: '8px' }}>Total Records (Including Children): {total}</Box>
            <GridFooter>
            </GridFooter>
        </GridFooterContainer>
    );
};

const getTreeDataPath: DataGridProProps["getTreeDataPath"] = (row) =>
    (row.treePath as string).split('/');

const returnUrl = `${process.env.REACT_APP_CLASSIC_URL}/Dashboard`;

export enum BulkUpdateModes {
    Status,
    Resources
}

interface IWorkItemBulkUpdate {
    mode: BulkUpdateModes
}

const PageTitleMode = new Map<BulkUpdateModes, string>([
    [BulkUpdateModes.Status, "Status"],
    [BulkUpdateModes.Resources, "Resources"]
]);

const WorkItemBulkUpdate = (props: IWorkItemBulkUpdate) => {
    const apiRef = useGridApiRef();
    const workItemPermissions = useBulkUpdateWorkItemPermissions();
    const mode = props.mode as BulkUpdateModes;
    const pageTitle = `Bulk Update Work Item - ${PageTitleMode.get(props.mode)}`;
    const [selectedWorkItems, setSelectedWorkItems] = useState<GridRowSelectionModel>([]);
    const [workItemsToUpdate, setWorkItemsToUpdate] = useState<WorkItem[]>([]);
    const [bulkUpdateStatus, setBulkUpdateStatus] = useState<number>(0);
    const [disableSave, setDisableSave] = useState<boolean>(false);
    const [openEmptySearchCriteriaNotificationDialog, setEmptySearchCriteriaNotificationDialog] = useState<boolean>(false);
    const [openSearchWithoutSaveConfirmation, setOpenSearchWithoutSaveConfirmation] = useState<boolean>(false);
    const { WorkItemBulkUpdateStatuses, WorkItemSearchStatuses, WorkItemStatuses, WorkItemStatusValidTransitions, WorkItemStatusLoading } = useWorkItemPossibleStatus();
    const [doSearchStatus, setDoSearchStatus] = useState<boolean>(false);
    const [doSearchResources, setDoSearchResources] = useState<boolean>(false);
    const [userSearched, setUserSearched] = useState<boolean>(false);
    const [taskTracking, setTaskTracking] = useState<string | undefined>(undefined);
    const [saveBulkUpdateStatusResult, saveBulkUpdateStatusInProgress, saveBulkUpdateStatusResultError, setSaveBulkUpdateStatusResult, saveBulkUpdateStatusWorkItems] = useSaveWorkItems(workItemsToUpdate, taskTracking);
    const [searchPayload, setSearchPayload] = useState<WorkItemSearchCriteria>({
        contentType: [0],
        resource: '',
        status: [0],
        workItemSearchText: '',
        workspaces: [],
        resourceOnlyAsPrimary: false,
        includeChildWorkItems: false,
        searchAll: true,
        userId: '',
        responsibilities: [],
        responsibilityUseOrOperator: false,
        matchOnPrimary: false
    });
    const [workItemsForBulkUpdateStatus,
        loadingWorkItemsForBulkUpdateStatus,
        errorWorkItemsForBulkUpdateStatus,
        setWorkItemsForBulkUpdateStatus] = useWorkItems(searchPayload,
            { page: 1, pageSize: 100 }, doSearchStatus, setDoSearchStatus, WorkItemSearchStatuses);

    const groupingColDef: DataGridProProps['groupingColDef'] = {
        headerName: 'Work Item ID',
        sortable: true,
        sortComparator: (a, b) => Number(a) - Number(b),
        headerClassName: 'header',
        disableColumnMenu: false,
        renderHeader: () => {
            return (
                <Tooltip title="Work Item ID" arrow>
                    <Box>{"Work Item ID"}</Box>
                </Tooltip>
            )
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <GroupByColumnRenderCell {...params} />
            )
        }
    };

    useEffect(() => {
        if (workItemsToUpdate && workItemsToUpdate.length > 0 && taskTracking) {
            saveBulkUpdateStatusWorkItems();
        }
    }, [workItemsToUpdate, taskTracking]);

    useEffect(() => {
        if (saveBulkUpdateStatusResult) {
            switch (saveBulkUpdateStatusResult.statusResponse) {
                case 'ok':
                    setBulkUpdateSucceeded(true);
                    break;
                case 'error':
                    setBulkUpdateErrored(true);
                    break;
                default:
                    break;
            }
        }
    }, [saveBulkUpdateStatusResult]);


    const cleanResults = () => {
        setWorkItemsToUpdate([]);
        setWorkItemsForBulkUpdateStatus(null);
        setWorkItemsForBulkUpdateResources(null)
        setSelectedWorkItems([]);
        setBulkUpdateSucceeded(false);
        setBulkUpdateErrored(false);
        setSaveBulkUpdateStatusResult(null);
        setDisableSave(false);

        setChangedWorkItems([]);
        setWorkItemsToUpdate([]);

        setSelectedWorkItems([]);
        setSelectedWorkSpaceIds([]);
        setBulkUpdateSucceeded(false);
        setBulkUpdateErrored(false);
        setSaveBulkUpdateResourcesResult(null)
        setSaveBulkUpdateStatusResult(null);
        setDisableSave(false);
    }

const handleSearch = (payload: WorkItemSearchCriteria) => {
        if (openSearchWithoutSaveConfirmation) {
            setOpenSearchWithoutSaveConfirmation(false);
        }

        if (payload.workItemSearchText === ''
            && payload.resource === ''
            && payload.workspaces.length === 0
            && payload.status.length === 0
            && payload.contentType.length === 0
            && payload.responsibilities.length === 0) {
            setEmptySearchCriteriaNotificationDialog(true);
            return;
        }

        cleanResults();            
        setSearchPayload(payload);
        switch(props.mode){
            case BulkUpdateModes.Status:
                setDoSearchStatus(true);
                break;
            case BulkUpdateModes.Resources:
                setDoSearchResources(true);
                break;
        }
        
        setTimeout(() => {
            setUserSearched(true);
        }, 10);
    }

    const handleClearGrid = () => {
        setDoSearchStatus(false);
        setDoSearchResources(false);
        cleanResults();
    }

    const handleUpdateGridStatus = () => {
        if (bulkUpdateStatus > 0) {
            selectedWorkItems.map(gridId => {
                var workItemToUpdate = workItemsForBulkUpdateStatus!.workItems!.find(x => x.treePath === gridId);
                if (workItemToUpdate) {
                    workItemToUpdate.errorMessage = null;
                    var fromStatus = workItemToUpdate?.originalSelectedValue != null && workItemToUpdate?.originalSelectedValue !== undefined ? workItemToUpdate?.originalSelectedValue : workItemToUpdate?.workItemStatusId;
                    var isTransitionAvailable = WorkItemStatusValidTransitions.filter(f => f.fromStatus === fromStatus && f.toStatus === bulkUpdateStatus);
                    if (isTransitionAvailable && isTransitionAvailable.length > 0) {
                        if(workItemToUpdate.originalSelectedValue === null || workItemToUpdate.originalSelectedValue === undefined)
                        {
                            workItemToUpdate.originalSelectedValue = workItemToUpdate.workItemStatusId;
                        }
                        workItemToUpdate.workItemStatusId = bulkUpdateStatus;
                    }
                    else {
                        workItemToUpdate.errorMessage = "The status transition to/from this status is not allowed";
                    }
                }
            });
            apiRef.current.updateRows(workItemsForBulkUpdateStatus!.workItems!);
        }
    }

    const onUpdateSelectedRows = (newRowSelectionModel: GridRowSelectionModel) => {
        var workItems = props.mode === BulkUpdateModes.Status ? workItemsForBulkUpdateStatus
            : props.mode === BulkUpdateModes.Resources ? workItemsForBulkUpdateResources :
                null;

        if (workItems === null || workItems === undefined) {
            return;
        }

        const deselectedIds = selectedWorkItems.filter(x => !newRowSelectionModel.includes(x));
        const newSelectedIds = newRowSelectionModel.filter(x => !selectedWorkItems.includes(x));
        const updatedRowSelectionModel = [...newRowSelectionModel];
        newSelectedIds.map(gridId => {
            const path = gridId.toString().split('/');
            const wiId = path[path.length - 1];
            const repeatedWorkItems = workItems!.workItems!.filter(x => x.workItemInstanceId === parseInt(wiId) && x.treePath !== gridId);
            repeatedWorkItems.forEach(x => {
                updatedRowSelectionModel.push(...repeatedWorkItems.map(x => x.treePath));
            });
        });

        deselectedIds.map(gridId => {
            const path = gridId.toString().split('/');
            const wiId = path[path.length - 1];
            const repeatedWorkItems = workItems!.workItems!.filter(x => x.workItemInstanceId === parseInt(wiId));
            repeatedWorkItems.forEach(x => {
                x.errorMessage = '';
                const index = newRowSelectionModel.findIndex(y => y === x.treePath);
                if (index > -1) {
                    updatedRowSelectionModel.splice(index, 1);
                }
            });
        });

        setSelectedWorkItems([...updatedRowSelectionModel.filter((value, index, array) => array.indexOf(value) === index)]);

        const workSpaceIds: number[] = [];
        newRowSelectionModel.forEach(treePath => {
            const workItem = workItems!.workItems!.find(x => x.treePath === treePath);
            if (workItem && !workSpaceIds.includes(workItem.workspaceInstanceId)) {
                workSpaceIds.push(workItem.workspaceInstanceId);
            }
        })
        setSelectedWorkSpaceIds(workSpaceIds);
    }

    const onSaveChanges = () => {
        const workItemsToSave: WorkItem[] = [];
        selectedWorkItems.forEach(gridId => {
            const path = gridId.toString().split('/');
            const wiId = path[path.length - 1];
            const existentWorkItem = workItemsToSave.findIndex(x => x.workItemInstanceId === parseInt(wiId));
            if (existentWorkItem === -1) {
                workItemsToSave.push({
                    workItemInstanceId: parseInt(wiId),
                    workItemStatusId: apiRef.current.getCellValue(gridId, 'workItemStatusId'),
                    treePath: gridId.toString(),
                    treeLevel: 0,
                    workItemInstanceName: '',
                    workItemTemplateId: 0,
                    workItemTemplateName: '',
                    workspaceInstanceId: 0,
                    workspaceInstanceName: '',
                    versionStamp: apiRef.current.getCellValue(gridId, "versionStamp"),
                    selected: true,
                    error: false,
                    errorMessage: '',
                    selectedResponsibility: 0,
                    isDirty: true,
                    parentTreePath: gridId.toString(),
                })
            } else {
                workItemsToSave[existentWorkItem].workItemStatusId = apiRef.current.getCellValue(gridId, 'workItemStatusId');
            }
        });
        setWorkItemsToUpdate(workItemsToSave);
        setTaskTracking(moment.utc().format('YYYY-MM-DDTHH:mm:ss'));
        setDisableSave(true);
    }

    const rowCellEdited = (params: any) => {
        const path = params.row.treePath.toString().split('/');
        const wiId = path[path.length - 1];
        const repeatedWorkItems = workItemsForBulkUpdateStatus!.workItems!.filter(x => x.workItemInstanceId === parseInt(wiId));
        const changedSelectionIds: string[] = [];
        if(repeatedWorkItems.length > 0){
            repeatedWorkItems.forEach(x => {
                if (!selectedWorkItems.includes(x.treePath)) {
                    changedSelectionIds.push(x.treePath);
                }
            });
        } else {
            changedSelectionIds.push(params.row.treePath);
        }
        setSelectedWorkItems(selectedWorkItems => ([...selectedWorkItems, ...changedSelectionIds]));
    }

    const validateRow = (props: GridRowProps) => {
        var [result, workItems] = mode === BulkUpdateModes.Status ? [saveBulkUpdateStatusResult, workItemsForBulkUpdateStatus] :
            mode === BulkUpdateModes.Resources ? [saveBulkUpdateResourcesResult, workItemsForBulkUpdateResources] : [null, null];

        const resultRowValidation = result?.results?.find(x => x.workItemInstanceId === props.row?.workItemInstanceId);
        const wiWithError = (workItems && workItems.workItems.length > 0) ? workItems.workItems.find(x => !!x.errorMessage && props.row?.workItemInstanceId == x.workItemInstanceId) : "";

        if (resultRowValidation && !resultRowValidation.valid) {
            return { checked: true, valid: false };
        }

        if (resultRowValidation && resultRowValidation.valid) {
            return { checked: true, valid: true };
        }

        if (wiWithError && (wiWithError as WorkItem).errorMessage) {
            return { checked: true, valid: false };
        }

        return { checked: false, valid: false };
    }

    const getRowErrorMessage = (props: GridRowProps) => {
        var [result, workItems] = mode === BulkUpdateModes.Status ? [saveBulkUpdateStatusResult, workItemsForBulkUpdateStatus] :
            mode === BulkUpdateModes.Resources ? [saveBulkUpdateResourcesResult, workItemsForBulkUpdateResources] : [null, null];

        const workItemValidations = result?.results?.filter(x => x.workItemInstanceId === props.row?.workItemInstanceId && !x.valid) ?? [];
        const wiWithError = (workItems && workItems.workItems.length > 0) ?
            workItems.workItems.find(x => x.errorMessage != null && props.row?.workItemInstanceId == x.workItemInstanceId) :
            "";

        var validationMessage = '';
        workItemValidations?.forEach(workItemValidation => {

            if (workItemValidation) {
                const responsibility = (props.row as WorkItemWithResources).responsibilitiesResources.find(x => x.responsibilityId === workItemValidation.responsibilityId);
                const resource = responsibility?.resources?.find(x => x.userId === workItemValidation.userId);

                if (workItemValidation.concurrency && mode === BulkUpdateModes.Resources) {
                    validationMessage += `Work item resource for Responsibility${responsibility ? ` ${responsibility?.responsibilityName}` : ''} was updated by someone else.`;
                }
                if (workItemValidation.concurrency && mode === BulkUpdateModes.Status) {
                    validationMessage += 'Work item was updated by someone else';
                }
                if (workItemValidation.resourceMissing) {
                    validationMessage += `${validationMessage === '' ? '' : ', '}Work item has no resource assigned`;
                }
                if (workItemValidation.successorTaskStatus) {
                    validationMessage += `${validationMessage === '' ? '' : ', '}The work item has either active/or completed successors`;
                }
                if (workItemValidation.statusTransitionPossible) {
                    validationMessage += `${validationMessage === '' ? '' : ', '}The status transition to/from this status is not allowed`;
                }
                if (workItemValidation.responsibilityNotInWorkItem && mode === BulkUpdateModes.Resources) {
                    validationMessage += `${validationMessage === '' ? '' : ', '}Responsibility does not exist in Work item.`;
                }
                if (workItemValidation.userNotInWorkSpace && mode === BulkUpdateModes.Resources) {
                    validationMessage += `${validationMessage === '' ? '' : ', '}The new resource added${!!resource ? ` ${resource.userName}` : ''} for Responsibility${responsibility ? ` ${responsibility?.responsibilityName}` : ''} does not exist in workspace.`;
                }
                if (workItemValidation.userNotFoundInWorkItem && mode === BulkUpdateModes.Resources) {
                    validationMessage += `${validationMessage === '' ? '' : ', '}Assigned resource${!!resource ? ` ${resource.userName}` : ''} for Responsibility${responsibility ? ` ${responsibility?.responsibilityName}` : ''} was neither new nor present in workitem.`;
                }
            }
        });
        return validationMessage ? validationMessage : (wiWithError ? wiWithError.errorMessage : null);
    }

    const onStatusColumnChange = (changeType: string, params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>, event: any): void => {
        if (changeType === 'status') {
            const rowId = params.row.id;
            const path = params.row.treePath.toString().split('/');
            const wiId = path[path.length - 1];
            const selectedStatusId = event.target.value;
            const repeatedWorkItems = apiRef.current.getSortedRows().filter(x => x.workItemInstanceId === parseInt(wiId));
            repeatedWorkItems.forEach(x => {
                x.originalSelectedValue = x.workItemStatusId; // original value
                x.workItemStatusId = selectedStatusId;
            });
            params.row.workItemStatusId = selectedStatusId;
        }
        
        rowCellEdited(params);
    }

    const bulkUpdateStatusColumns = useWorkItemStatusColumns(onStatusColumnChange, apiRef, WorkItemStatuses, WorkItemStatusValidTransitions, workItemPermissions);

    /** Resorces &  Responsibility */
    const [filteredResponsibilities, setFilteredResponsibilities] = useState<{ id: number, name: string }[]>([]);
    const [changedWorkItems, setChangedWorkItems] = useState<IChangedWorkItemResourceAssigne[]>([]);
    const [selectedWorkSpaceIds, setSelectedWorkSpaceIds] = useState<number[]>([]);
    const [workItemsForBulkUpdateResources, loadingWorkItemsForBulkUpdateResources, errorWorkItemsForBulkUpdateResources, setWorkItemsForBulkUpdateResources] = useWorkItemsWithResources(searchPayload, { page: 1, pageSize: 100 }, doSearchResources, setDoSearchResources);
    const [resourceWorkItemsToUpdate, setResourceWorkItemsToUpdate] = useState<ISaveWorkItemResourceAssigne[]>([]);
    const [saveBulkUpdateResourcesResult, saveBulkUpdateResourcesInProgress, saveBulkUpdateResourcesResultError, setSaveBulkUpdateResourcesResult, saveBulkUpdateResourcesWorkItems] = useBulkUpdateSaveWorkItemResources(resourceWorkItemsToUpdate, taskTracking);
    const [drawer, setDrawer] = useState(false);

    useEffect(() => {
        const distinctResponsibilities: { id: number; name: string; }[] = [];
        selectedWorkItems?.forEach(selectedId => {
            const workItem = workItemsForBulkUpdateResources?.workItems.find(wi => wi.treePath === selectedId);
            if (workItem) {
                workItem.responsibilitiesResources.forEach(responsibility => {
                    const existingResponsibility = distinctResponsibilities.find(item => item.id === responsibility.responsibilityId);
                    if (!existingResponsibility) {
                        distinctResponsibilities.push({
                            id: responsibility.responsibilityId,
                            name: responsibility.responsibilityName
                        });
                    }
                });
            }
        });

        if (distinctResponsibilities && distinctResponsibilities.length > 0) {
            distinctResponsibilities.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase()) ? -1 : ((a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : 0));
        }
        setFilteredResponsibilities(distinctResponsibilities);
    }, [selectedWorkItems, workItemsForBulkUpdateResources]);

    useEffect(() => {
        if (resourceWorkItemsToUpdate && resourceWorkItemsToUpdate.length > 0 && taskTracking) {
            saveBulkUpdateResourcesWorkItems();
        }
    }, [resourceWorkItemsToUpdate, taskTracking]);

    useEffect(() => {
        if (saveBulkUpdateResourcesResult) {
            switch (saveBulkUpdateResourcesResult.statusResponse) {
                case 'ok':
                    setBulkUpdateSucceeded(true);
                    break;
                case 'error':
                    setBulkUpdateErrored(true);
                    break;
                default:
                    break;
            }
        }
    }, [saveBulkUpdateResourcesResult]);

    useEffect(() => {
        if (mode === BulkUpdateModes.Resources) {
            setChangedData();
        }
    }, [selectedWorkItems]);

    const setChangedData = (): void => {
        if (!apiRef?.current?.hasOwnProperty('getSelectedRows')) {
            return;
        }
        const changedItems: IChangedWorkItemResourceAssigne[] = [];
        apiRef.current.getSelectedRows().forEach((selectedItem) => {
            if (!(selectedItem as WorkItemWithResources)) {
                return;
            }
            const workItemInstanceId = (selectedItem as WorkItemWithResources).workItemInstanceId;
            if (changedItems.find(x => x.workItemInstanceId === workItemInstanceId)) {
                return;
            }
            (selectedItem as WorkItemWithResources).responsibilitiesResources.forEach((responsibility) => {
                const hasNew = !!responsibility.resources?.find((resource) => resource.isNew);
                const primaryChange = !!responsibility.resources?.find((resource) => (resource.selected && !resource.isDefaultAssignee));
                const isChanged = hasNew || primaryChange;
                if (isChanged) {
                    const changedItem: IChangedWorkItemResourceAssigne = {
                        id: ((selectedItem.workItemInstanceId?.toString() ?? 'workItemInstanceId') + (responsibility?.responsibilityId ?? '1') + (responsibility?.resources?.find((y: AssignedUser) => y.isDefaultAssignee)?.userId ?? 'userId')),
                        workItemInstanceId: workItemInstanceId,
                        workItemInstanceName: selectedItem.workItemInstanceName,
                        responsibility: responsibility.responsibilityName,
                        responsibilityId: responsibility.responsibilityId,
                        previousPrimaryResource: responsibility?.resources?.find((y: any) => y.isDefaultAssignee)?.userName ?? 'N/A',
                        currentPrimaryResource: !primaryChange ? undefined : ('(P) ' + responsibility?.resources?.find((y: any) => y.selected)?.userName),
                        newAlternateResources: responsibility.resources?.filter((resource: any) => resource.isNew && !resource.selected).map((x: any) => x.userName),
                        isAssignOnInProgress: responsibility.resources?.find((resource: any) => resource.selected)?.isAssignOnInProgress ?? false
                    };
                    changedItems.push(changedItem);
                }
            });
        })
        setChangedWorkItems(changedItems);
    }

    const handleBulkOperationUpdateGridResource = (responsibility: number[], resource: any, primary: boolean, assignOnInProgress: boolean) => {
        const boResource = resource as GetAutocompleteResult;
        const selectedResource: AssignedUser = {
            userId: boResource.userId,
            userName: boResource.fullName,
            isDefaultAssignee: false,
            selected: primary,
            isNew: false,
            isAssignOnInProgress: assignOnInProgress
        };
        selectedWorkItems.map(gridId => {
            var wi = workItemsForBulkUpdateResources!.workItems!.find(x => x.treePath === gridId);
            if (wi != null && responsibility) {
                const isUpdated = validateAndUpdateWorkItem(wi, selectedResource, boResource, responsibility);
                if (isUpdated) {
                    wi.rowChanged = checkIfRowChanged(wi);
                }
            }
        });
        apiRef.current.updateRows(workItemsForBulkUpdateResources!.workItems!);
        setChangedData();
    }

    const validateAndUpdateWorkItem = (wi: WorkItemWithResources,
        selectedResource: AssignedUser,
        boResource: GetAutocompleteResult,
        responsibility: number[]): boolean => {
        let isUpdated = true;
        let isChangePresent = false;
        responsibility.map(boResponsibility => {
            wi.errorMessage = null;
            //const boResponsibility = responsibility.at(0) as number;
            const responsibilityOnWi = wi?.responsibilitiesResources?.find(resp => resp.responsibilityId == boResponsibility);
            if (responsibilityOnWi !== null && responsibilityOnWi !== undefined) {
                const currentAssignee = responsibilityOnWi.resources.find(df => df.selected);
                const selectedResourceAlreadyPresent = responsibilityOnWi.resources.find(rr => rr.userId == selectedResource.userId);
                const newRewsourceWorkspaceIds = boResource.workspaceInstanceIds?.split(',');

                if (newRewsourceWorkspaceIds !== undefined && newRewsourceWorkspaceIds.indexOf('' + wi.workspaceInstanceId) < 0) {
                    wi.errorMessage = "The resource selected is not in the work item’s Workspace.";
                    wi.error = true;
                    isUpdated = false;
                } else if (!selectedResourceAlreadyPresent) {
                    selectedResource.isNew = true;
                    if (selectedResource.selected) {
                        if (currentAssignee) {
                            currentAssignee.selected = false;
                        }
                    }
                    //If responsibility dont have resource at work item
                    if (responsibilityOnWi.resources.length === 1 && responsibilityOnWi.resources[0].userId === '') {
                        responsibilityOnWi.resources[0].selected = true;
                        responsibilityOnWi.resources[0].isDefaultAssignee = false;
                        responsibilityOnWi.resources[0].isNew = true;
                        responsibilityOnWi.resources[0].userId = selectedResource.userId;
                        responsibilityOnWi.resources[0].userName = selectedResource.userName;
                        responsibilityOnWi.resources[0].isAssignOnInProgress = selectedResource.isAssignOnInProgress;
                        addToNewResponsibility(wi, responsibilityOnWi, selectedResource);
                    }
                    else {
                        responsibilityOnWi.resources.push(selectedResource);
                        addToNewResponsibility(wi, responsibilityOnWi, selectedResource);
                    }
                    wi.selectedResponsibilityId = boResponsibility;
                    wi.selectedResponsibility = responsibilityOnWi;
                }
                else {
                    //Update case
                    if(selectedResourceAlreadyPresent != undefined && selectedResourceAlreadyPresent != null) {
                        isChangePresent = true;
                    }
                    if(wi.newResponsibilitiesResources != undefined && wi.newResponsibilitiesResources.filter(f => f.responsibilityId === responsibilityOnWi.responsibilityId).length > 0)
                    {
                        var addedResources = wi.newResponsibilitiesResources.filter(f => f.responsibilityId === responsibilityOnWi.responsibilityId)[0].resources;
                        if(addedResources !== undefined && addedResources.filter(f => f.userId == selectedResource.userId))
                        {
                            isChangePresent = true;
                        }
                    }
                    if (selectedResourceAlreadyPresent.selected !== selectedResource.selected) {
                        selectedResourceAlreadyPresent.selected = selectedResource.selected;
                        selectedResourceAlreadyPresent.isAssignOnInProgress = selectedResource.isAssignOnInProgress;
                        if (currentAssignee) {
                            currentAssignee.selected = false;
                        }
                    }
                    else if (!isChangePresent && selectedResourceAlreadyPresent.selected && selectedResource.selected) {
                        selectedResourceAlreadyPresent.selected = true;
                        wi.errorMessage = "The resource is already the primary resource for this responsibility";
                        isUpdated = false;
                    }
                    else if (!isChangePresent && !selectedResourceAlreadyPresent.selected && !selectedResource.selected) {
                        selectedResourceAlreadyPresent.selected = false;
                        wi.errorMessage = "The resource is already alternative resource for this responsibility";
                        isUpdated = false;
                    }
                    if (currentAssignee && currentAssignee.userId == selectedResource.userId && !selectedResource.selected) {
                        selectedResourceAlreadyPresent.selected = true;
                        // User which is being updated was the default assignee and is now being made not default is not valid state
                        wi.errorMessage = "The request will result in no primary resource assignment";
                        isUpdated = false;
                    }
                }
            }
            else {
                wi.errorMessage = "Responsibility doesn't exist on Work item";
                isUpdated = false;
            }

        });
        return isUpdated;
    }

    const addToNewResponsibility = (wi : WorkItemWithResources, responsibilityOnWi : ResponsibilityWithResources, selectedResource : AssignedUser ) => {
        if(wi.newResponsibilitiesResources === undefined || wi.newResponsibilitiesResources.length <= 0)
        {
            wi.newResponsibilitiesResources = [];
            const newRespResouce :ResponsibilityWithResources = {
                responsibilityId : responsibilityOnWi.responsibilityId,
                responsibilityName : responsibilityOnWi.responsibilityName,
                resources: [selectedResource]
            };
            wi.newResponsibilitiesResources.push(newRespResouce);
        }
    }
    const checkIfRowChanged = (row: any) => {
        let result = false;

        for (let i = 0; i < row.responsibilitiesResources.length; i++) {

            let responsibilityWithResources = row.responsibilitiesResources[i];

            for (let j = 0; j < responsibilityWithResources.resources.length; j++) {
                let resource = responsibilityWithResources.resources[j];

                if (resource.selected == true && resource.isDefaultAssignee == false) {
                    result = true;
                    break;
                }

                if (resource.isNew) {
                    result = true;
                    break;
                }
            }

            if (result == true)
                break;
        }

        return result;
    };

    const onResourceChange = (eventObj: any, params: any) => {
        const rowId = params.row.id;
        const path = rowId.toString().split('/');
        const wiId = path[path.length - 1];
        const selectedResourceId = eventObj.target.value;
        const repeatedWorkItems: WorkItemWithResources[] = params.api.getSortedRows().filter((x: WorkItemWithResources) => x.workItemInstanceId === parseInt(wiId));
        const updates: GridRowModelUpdate[] = [];
        const changedSelectionIds: string[] = [];
        const selectedItems = Array.from(apiRef.current.getSelectedRows().values()).map(x => x.treePath);
        repeatedWorkItems.forEach(row => {
            const responsibilitiesResources = row.responsibilitiesResources;
            const updatedresponsibilitiesResources = responsibilitiesResources.map((responsibilitiesResource: any) => {

                if (responsibilitiesResource.responsibilityId != row.selectedResponsibilityId) {
                    return responsibilitiesResource
                }
                else {
                    let resources = responsibilitiesResource.resources;
                    var newResources = resources.map((resource: any) => {
                        if (resource.userId == selectedResourceId)
                            return { ...resource, selected: true }
                        else
                            return { ...resource, selected: false }
                    });

                    responsibilitiesResource.resources = newResources;
                    row.newResponsibilitiesResources = [];
                    const newRespResouce :ResponsibilityWithResources = {
                        responsibilityId : responsibilitiesResource.responsibilityId,
                        responsibilityName : responsibilitiesResource.responsibilityName,
                        resources: [newResources]
                    };
                    row.newResponsibilitiesResources.push(newRespResouce);
                    return responsibilitiesResource;
                }
            });
            row.rowChanged = checkIfRowChanged(row);
            updates.push({ id: row.id, responsibilitiesResources: updatedresponsibilitiesResources });
            if (!selectedItems.includes(row.treePath)) {
                changedSelectionIds.push(row.treePath);
            }
        });
        setSelectedWorkItems([...selectedItems, ...changedSelectionIds]);
        apiRef.current.updateRows(updates);
        setChangedData();
    };

    const onChange = (changeType: string, params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>, event: any): void => {
        if (changeType === 'responsibility') {
            const rowId = params.row.id;
            const path = rowId.toString().split('/');
            const wiId = path[path.length - 1];
            const selectedResponsibilityId = event.target.value;
            const repeatedWorkItems: any[] = params.api.getSortedRows().filter((x: any) => x.workItemInstanceId === parseInt(wiId));
            repeatedWorkItems.forEach(row => {
                row.selectedResponsibilityId = selectedResponsibilityId;
            });

            params.row.selectedResponsibilityId = selectedResponsibilityId;
            apiRef.current.setCellFocus(params.row.id, 'resources');
        }

        if (changeType === 'resource') {
            onResourceChange(event, params);
        }

    }

    const onReviewChangesConfirm = () => {
        if (!apiRef?.current?.hasOwnProperty('getSelectedRows')) {
            return;
        }
        const workItemsToSave: ISaveWorkItemResourceAssigne[] = [];
        apiRef.current.getSelectedRows().forEach((selectedItem) => {
            const workItemInstanceId = (selectedItem as WorkItemWithResources).workItemInstanceId;
            if (workItemsToSave.find(x => x.workItemInstanceId === workItemInstanceId)) {
                return;
            }
            (selectedItem as WorkItemWithResources).responsibilitiesResources.forEach((responsibility) => {
                const currentUser = responsibility.resources.find(x => x.isDefaultAssignee);
                const currentUserId = currentUser?.userId;
                const currentUserName = currentUser?.userName;
                responsibility.resources.forEach(resource => {
                    const isChanged = resource.selected && !resource.isDefaultAssignee || resource.isNew;
                    if (isChanged) {
                        const changedItem: ISaveWorkItemResourceAssigne = {
                            workspaceId: selectedItem.workspaceInstanceId,
                            workItemInstanceId: selectedItem.workItemInstanceId,
                            responsibilityId: responsibility.responsibilityId,
                            isDefaultAssignee: resource.selected,
                            isNew: resource.isNew,
                            userId: resource.userId,
                            fromUserId: resource.selected && !resource.isDefaultAssignee ? currentUserId : undefined,
                            fromUserName: resource.selected && !resource.isDefaultAssignee ? currentUserName : undefined,
                            versionStamp: resource.versionStamp,
                            isAssignOnInProgress: resource.isAssignOnInProgress
                        };
                        workItemsToSave.push(changedItem);
                    }
                });
            })
        })
        if (workItemsToSave.length > 0) {
            setResourceWorkItemsToUpdate(workItemsToSave);
            setTaskTracking(moment.utc().format('YYYY-MM-DDTHH:mm:ss'));
            setDisableSave(true);
        }
        closeDrawer()
    }

    const onReviewChanges = () => {
        openDrawer();
    }

    const openDrawer = () => {
        setDrawer(true);
    }

    const closeDrawer = () => {
        setDrawer(false);
    }

    const bulkUpdateResourcesColumns = useWorkItemResourcesColumns(onChange, apiRef, workItemPermissions);

    /** COMMON */

    const [savingBulkUpdates, setSavingBulkUpdates] = useState<boolean>(false);
    const [bulkUpdateSucceeded, setBulkUpdateSucceeded] = useState<boolean>(false);
    const [bulkUpdateErrored, setBulkUpdateErrored] = useState<boolean>(false);

    useEffect(() => {
        setSavingBulkUpdates(saveBulkUpdateStatusInProgress || saveBulkUpdateResourcesInProgress);
    }, [saveBulkUpdateStatusInProgress, saveBulkUpdateResourcesInProgress]);

    const getData: (() => GridRowModel[]) = () => {
        switch (props.mode) {
            case BulkUpdateModes.Status:
                return getStatusData();
            case BulkUpdateModes.Resources:
                return getResourcesData();
        }
    }

    const getStatusData: (() => GridRowModel[]) = useMemo(() => {
        return () => (loadingWorkItemsForBulkUpdateStatus ? [] : (workItemsForBulkUpdateStatus?.workItems ?? []))
    }, [workItemsForBulkUpdateStatus]);

    const getResourcesData: (() => GridRowModel[]) = useMemo(() => {
        return () => (loadingWorkItemsForBulkUpdateResources ? [] : (workItemsForBulkUpdateResources?.workItems ?? []))
    }, [workItemsForBulkUpdateResources]);

    const getColumns: (() => GridColDef[]) = () => {
        switch (props.mode) {
            case BulkUpdateModes.Status:
                return bulkUpdateStatusColumns;
            case BulkUpdateModes.Resources:
                return bulkUpdateResourcesColumns;
        }
    }

    const pageTitleUI = (
        <>
            <Box sx={{ display: "flex", maxWidth: '85vw' }}>
                <PageTitle sx={{ color: '#001A69' }}>{pageTitle}</PageTitle>
            </Box>
        </>
    );

    const onHandleSearchStatus = (payload: WorkItemSearchCriteria) => {

        const isSearchCriteriaChange = searchCriteriaChange(payload);

        if (isSearchCriteriaChange) {
            handleSearch(payload);
        }
        else if (selectedWorkItems.length > 0 && !isSearchCriteriaChange) {

            setOpenSearchWithoutSaveConfirmation(true);
        }
        else {
            handleSearch(payload);
        }
    }

    const searchCriteriaChange = (payload: WorkItemSearchCriteria) => {
        var isSearchCriteriaChange = false;
        if (payload.workspaces.length !== searchPayload.workspaces.length) {
            isSearchCriteriaChange = true;
        }
        if (payload.workItemSearchText !== searchPayload.workItemSearchText) {
            isSearchCriteriaChange = true;
        }
        if (payload.status.length !== searchPayload.status.length
            &&
            !(searchPayload.status.length === 1 && searchPayload.status[0] === 0)) {
            isSearchCriteriaChange = true;
        }

        if (payload.resource !== searchPayload.resource) {
            isSearchCriteriaChange = true;
        }
        if (payload.contentType.length !== searchPayload.contentType.length
            &&
            !(searchPayload.contentType.length === 1 && searchPayload.contentType[0] === 0)) {
            isSearchCriteriaChange = true;
        }
        return isSearchCriteriaChange;
    }

             const searchUI = (
        <>
            <Box sx={{ pl: '20px' }} id="work-item-search-box">
                <Grid
                    container
                    spacing={{ xs: 2, md: 2 }}
                    columns={{ xs: 1, sm: 1, md: 12, lg: 12 }}>
                    <BulkUpdateSearch
                        mode={props.mode}
                        onClearSearch={handleClearGrid}
                         onSearch={(payload) => onHandleSearchStatus(payload)}
                    />
                </Grid>
            </Box>
        </>
    );

    const workItemsDataGrid = (
        <>
            <Box id="work-item-grid-box">
                <GridBox getData={getData}>
                    {
                        <Box sx={{ height: "80vh" }}>
                            <DataGridPro
                                sx={{
                                    '& .MuiDataGrid-columnHeaderCheckbox': {
                                        pl: '0px', pr: '5px'
                                    },
                                    '& .MuiDataGrid-cellCheckbox': {
                                        pl: '15px',
                                        '& .Mui-disabled': {
                                            display: 'none'
                                        }
                                    }
                                        
                                }}
                                apiRef={apiRef}
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: '__tree_data_group__', sort: 'asc' }]   

                                    }
                                }}
                                disableColumnFilter
                                disableColumnSelector
                                disableDensitySelector
                                disableColumnPinning
                                rows={getData()}
                                columns={getColumns()}
                                treeData
                                getTreeDataPath={getTreeDataPath}
                                getRowId={(row: any) => mode === BulkUpdateModes.Status ? row.treePath : row.id}
                                groupingColDef={groupingColDef}
                                checkboxSelection
                                isRowSelectable={(params) => { return mode === BulkUpdateModes.Status ? (WorkItemSearchStatuses.find(x => x.id === params.row.workItemStatusId) !== undefined) : true }}
                                isCellEditable={(params) => { return mode === BulkUpdateModes.Status ? (WorkItemSearchStatuses.find(x => x.id === params.row.workItemStatusId) !== undefined) : true }}
                                disableRowSelectionOnClick
                                keepNonExistentRowsSelected={true}
                                rowSelectionModel={selectedWorkItems}
                                onCellEditStop={rowCellEdited}
                                onRowSelectionModelChange={(newRowSelectionModel) => {
                                    onUpdateSelectedRows(newRowSelectionModel);
                                }}
                                pageSizeOptions={[25, 50, 75, 100]}
                                pagination={getData() && getData().length > 0}
                                slots={{
                                    loadingOverlay: () => (<CircularProgress/>),
                                    columnUnsortedIcon: null,
                                    columnMenuSortAscendingIcon: Ico_SortAscending,
                                    columnMenuSortDescendingIcon: Ico_SortDescending,
                                    columnSortedAscendingIcon: Ico_SortAscending,
                                    columnSortedDescendingIcon: Ico_SortDescending,
                                    quickFilterIcon: Ico_Filter_Component,
                                    columnFilteredIcon: Ico_Filter_Component,
                                    quickFilterClearIcon: Ico_Filter_Component,
                                    columnMenuFilterIcon: Ico_Filter_Component,
                                    noRowsOverlay: () => NoRowsOverlay(userSearched),
                                    toolbar: GridToolbar,
                                    row: (props) => WorkItemRow({ props, validateRow, getRowErrorMessage }),
                                    footer: () => CustomFooter(getData().length)
                                }}
                                loading={loadingWorkItemsForBulkUpdateStatus || loadingWorkItemsForBulkUpdateResources}
                                slotProps={{panel: {placement: "bottom-end"}}}
                            />
                        </Box>

                    }
                </GridBox>
            </Box>
        </>
    );

    const bulkUpdateStatusActionsUI = (
        <>
            <Grid container spacing={{ xs: 2, md: 2 }} columns={{ xs: 1, sm: 1, md: 12, lg: 12 }}>
                <Grid xs={1} sm={1} md={8} lg={6}>
                </Grid>
                <Grid xs={1} sm={1} md={12} lg={12} my={'24px'} flexDirection='row-reverse' container>
                    <Button
                        id={"bulk-update-back-" + BulkUpdateModes[props.mode]}
                        href={returnUrl}
                        sx={{ marginRight: '10px' }}
                        variant="outlined">Back</Button>
                    {selectedWorkItems && selectedWorkItems.length > 0 &&
                        <Button
                            id={"bulk-update-save-" + BulkUpdateModes[props.mode]}
                            sx={{ marginRight: '10px' }}
                            variant="contained" disabled={disableSave} onClick={() => { onSaveChanges() }} >Save</Button>}
                </Grid>
            </Grid>
        </>
    );

    const bulkUpdateResourcesActionsUI = (
        <>
            {selectedWorkItems && selectedWorkItems.length > 0 &&
                <Grid
                    container
                    spacing={{ xs: 2, md: 2 }}
                    columns={{ xs: 1, sm: 1, md: 12, lg: 12 }}>
                    <Grid xs={1} sm={1} md={8} lg={6}>
                    </Grid>
                    <Grid xs={1} sm={1} md={12} lg={12} my={'24px'}
                        flexDirection='row-reverse'
                        container>
                        <Tooltip title={(!changedWorkItems || changedWorkItems.length === 0) ? 'No changes to review' : ''}>
                            <span>
                                <Button
                                    id={"bulk-update-review-changes-" + BulkUpdateModes[props.mode]}
                                    variant="contained"
                                    sx={{ marginRight: '10px', pointerEvents: (changedWorkItems?.length === 0 || disableSave) ? 'none' : 'initial' }}
                                    disabled={!changedWorkItems || changedWorkItems.length === 0 || disableSave}
                                    onClick={onReviewChanges}>
                                    Review Changes
                                </Button>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            }
        </>
    );

    const bulkUpdateOperationsUI = (
        <>
            {
                (workItemsForBulkUpdateStatus?.workItems || workItemsForBulkUpdateResources?.workItems) &&
                <Box
                    sx={{
                        mt: '25px',
                        borderRadius: 6,
                        backgroundColor: 'lightblue',
                        pb: '15px',
                        pl: '35px',
                        pr: '-35px'
                    }}
                    id="work-item-bulk-update-box">
                    <Grid
                        container
                        spacing={{ xs: 2, md: 2 }}
                        columns={{ xs: 1, sm: 1, md: 12, lg: 12 }}>

                        {props.mode == BulkUpdateModes.Status &&
                            <BulkUpdateOpertionsStatus
                                idSuffix={BulkUpdateModes[props.mode]}
                                statusOptions={WorkItemBulkUpdateStatuses}

                                disabled={bulkUpdateStatus <= 0 || !selectedWorkItems || selectedWorkItems.length <= 0}
                                onBulkUpdateStatusChange={(statuses: number[]) => setBulkUpdateStatus(statuses[0])}
                                onUpdateGridClick={() => handleUpdateGridStatus()} />
                        }

                        {/* 
                         * WILL BE CLEANED UP IN THE BFLY-12189.
                         *
                        {props.mode === BulkUpdateModes.Resources &&
                            <BulkUpdateOpertionsResponsibilities
                                idSuffix={BulkUpdateModes[props.mode]}
                                responsibilityOptions={filteredResponsibilities}
                                selectedWorkItems={selectedWorkItems}
                                selectedWorkSpaceIds={selectedWorkSpaceIds}
                                OnReviewConfirmClick={handleBulkOperationUpdateGridResource} />
                        } */}

                    </Grid>
                </Box>
            }
        </>
    );

    const DialoguUI = (title: string, opener: boolean, onClose: any, actions: { Text: string, Variant: any, onClick: () => void }[]) => {
        return (
            <>
                <Dialog
                    fullWidth={false}
                    maxWidth={'lg'}
                    onClose={onClose}
                    open={opener}>
                    <DialogTitle
                        sx={{ textAlign: 'left', fontWeight: '600', fontSize: '17px' }}>
                        {<Ico_Warning color='error' sx={{ height: '40px', width: '40px' }} />}
                        <br />
                        {title}
                    </DialogTitle>
                    <DialogContent>
                        <DialogActions
                            sx={{ paddingTop: '10px', width: '100%' }}>
                            {actions.map((action) => (
                                <Button
                                    fullWidth
                                    variant={action.Variant}
                                    onClick={action.onClick}>
                                    {action.Text}
                                </Button>
                            ))
                            }
                        </DialogActions>
                    </DialogContent>
                </Dialog>
            </>
        );
    }

    return (
        <div>
            <Helmet>
                <title>Sage Central | Bulk Update Work Item Status</title>
            </Helmet>
            {pageTitleUI}

            {searchUI}

            {workItemsDataGrid}

            {bulkUpdateSucceeded && <Alert severity="success" onClose={() => setBulkUpdateSucceeded(false)}>Work items saved.</Alert>}
            {bulkUpdateErrored && <Alert severity="error" onClose={() => setBulkUpdateErrored(false)}>{saveBulkUpdateStatusResult?.errorMessage && saveBulkUpdateStatusResult?.errorMessage !== '' ? saveBulkUpdateStatusResult.errorMessage : 'There has been an error trying to save the changes.'}</Alert>}
            {savingBulkUpdates &&
                <span>
                    <CircularProgress sx={{ left: 0, right: 0 }} color="primary" />
                    <span style={{ margin: '10px 0 0 50px', display: 'inline-block', position: 'absolute' }}>Update in progress..</span>
                </span>
            }

            {props.mode === BulkUpdateModes.Status && bulkUpdateStatusActionsUI}

            {props.mode === BulkUpdateModes.Resources && bulkUpdateResourcesActionsUI}

            {bulkUpdateOperationsUI}

            {DialoguUI("You must enter at least 1 search item",
                openEmptySearchCriteriaNotificationDialog,
                setEmptySearchCriteriaNotificationDialog,
                [
                    {
                        Text: "Ok",
                        Variant: "contained",
                        onClick: () => setEmptySearchCriteriaNotificationDialog(!openEmptySearchCriteriaNotificationDialog)
                    }
                ]
            )
            }

            {DialoguUI("No changes will be made to selected work items.",
                openSearchWithoutSaveConfirmation,
                setOpenSearchWithoutSaveConfirmation,
                [
                    {
                        Text: "Continue",
                        Variant: "contained",
                        onClick: () => handleSearch(searchPayload)
                    },
                    {
                        Text: "Cancel",
                        Variant: "outlined",
                        onClick: () => setOpenSearchWithoutSaveConfirmation(!openSearchWithoutSaveConfirmation)
                    }
                ])
            }

            {changedWorkItems?.length > 0 &&
                (<WorkItemResourceReviewChanges                    
                    open={drawer}
                    onClose={closeDrawer}
                    onConfirm={onReviewChangesConfirm}
                    onEdit={closeDrawer}
                    changedItems={changedWorkItems} />)
            }
        </div>
    )
};

export default WorkItemBulkUpdate;